import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  HTTP_STATUSES,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  AccountType,
  CalendarEventType,
  ICalendarSet,
  IVerticalTabProps,
  PlatformModules,
} from "@miplatform/types";
import { getUpdatedFields } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiAlertCircleOutline, mdiChevronDown } from "@mdi/js";
import {
  useAccounts,
  useCalendarSets,
  useOrderTemplates,
  usePermissions,
  useStepTemplates,
  useWorkOrderTemplate,
} from "@mibuildweb/hooks";
import { Chip, Typography, useTheme } from "@mui/material";
import { VerticalTabsLayout } from "../../../../components";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { ConfigMenu } from "../../components";
import { IWorkOrderTemplateData } from "../ConfigWorkOrderTemplates.types";
import { configWorkOrderTemplateSchema } from "../schemas/ConfigWorkOrderTemplate.schema";
import { WorkOrderAssignmentRulesTabView } from "../tabs/WorkOrderAssignmentRulesTabView";
import { WorkOrderCustomerExclusionTabView } from "../tabs/WorkOrderCustomerExclusionTabView";
import { WorkOrderDetailsTabView } from "../tabs/WorkOrderDetailsTabView";
import { WorkOrderSchedulingRulesTabView } from "../tabs/WorkOrderSchedulingRulesTabView";
import { WorkOrderStepsTabView } from "../tabs/WorkOrderStepsTabView";
import { useWorkOrderTemplateDetailsViewModel } from "./useWorkOrderTemplateDetailsViewModel";

export function ConfigWorkOrderTemplateDetails({
  module,
}: Readonly<{ module: PlatformModules }>) {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const { params, goBack } = useRouter();
  const { id: workOrderTemplateId } = params;
  const theme = useTheme();

  const { hasPermission } = usePermissions();
  const isReadOnly =
    (!workOrderTemplateId &&
      !hasPermission(PERMISSIONS.WORK_ORDER_TEMPLATE.CREATE)) ||
    (!!workOrderTemplateId &&
      !hasPermission(PERMISSIONS.WORK_ORDER_TEMPLATE.UPDATE));

  const form = useForm<IWorkOrderTemplateData>({
    mode: "onChange",
    defaultValues: { ...defaultWorkOrderTemplateData, module },
    resolver: zodResolver(configWorkOrderTemplateSchema),
  });

  const {
    formState: { isValid, isDirty, dirtyFields },
    setValue,
    getValues,
    reset,
    watch,
  } = form;

  const { isActive, id, name, orderTemplateId, autoAssign } = watch();

  const {
    isLoading: isViewModelLoading,
    handleCreateWorkOrderTemplate,
    fetchExcludedAccounts,
    excludedAccounts,
    excludeWorkOrderTemplate,
    reenableWorkOrderTemplate,
  } = useWorkOrderTemplateDetailsViewModel({
    module,
    workOrderTemplateId: workOrderTemplateId ?? undefined,
  });

  const {
    isLoading: isWorkOrderTemplatesLoading,
    workOrderTemplateDetails,
    getWorkOrderTemplateDetails,
    updateWorkOrderTemplate,
  } = useWorkOrderTemplate({
    module,
  });

  const assignmentTemplateId =
    workOrderTemplateDetails?.assignmentTemplates?.[0]?.id ?? "";
  const assignmentTemplateModifiedOn =
    workOrderTemplateDetails?.assignmentTemplates?.[0]?.modifiedOn ?? "";

  const {
    stepTemplates,
    getStepTemplates,
    reorderStepTemplates,
    isLoading: isStepsLoading,
  } = useStepTemplates({
    assignmentTemplateId,
  });

  const { getOrderTemplates, setOrderTemplates, orderTemplates } =
    useOrderTemplates({ module });

  const {
    getAccounts,
    accounts,
    isLoading: isAccountsLoading,
  } = useAccounts({
    accountTypes: [AccountType.Builder],
  });

  const {
    calendarSets,
    getCalendarSets,
    setCalendarSets,
    isLoading: isCalendarSetsLoading,
  } = useCalendarSets({ module, eventType: CalendarEventType.WorkOrder });

  const selectedCalendarSets = useMemo<ICalendarSet[]>(
    () => calendarSets,
    [calendarSets]
  );

  useEffect(() => {
    if (workOrderTemplateId) {
      getWorkOrderTemplateDetails(workOrderTemplateId, reset);
      fetchExcludedAccounts();
    }
  }, [workOrderTemplateId]);

  useEffect(() => {
    if (assignmentTemplateId) {
      getStepTemplates();
    }
  }, [assignmentTemplateId]);

  useEffect(() => {
    setValue("steps", stepTemplates, { shouldDirty: false });
  }, [stepTemplates]);

  useEffect(() => {
    const fetchOrderTemplates = async () => {
      const response = await getOrderTemplates();
      if (response?.data) {
        setOrderTemplates(response.data);
      }
    };

    const fetchCalendarSets = async () => {
      const response = await getCalendarSets({});
      if (response?.status === HTTP_STATUSES.OK) {
        setCalendarSets(response.data);
      }
    };

    fetchOrderTemplates();
    getAccounts();
    fetchCalendarSets();
  }, []);

  const isLoading = useMemo(
    () =>
      isViewModelLoading ||
      isWorkOrderTemplatesLoading ||
      isStepsLoading ||
      isAccountsLoading ||
      isCalendarSetsLoading,
    [
      isViewModelLoading,
      isWorkOrderTemplatesLoading,
      isStepsLoading,
      isAccountsLoading,
      isCalendarSetsLoading,
    ]
  );

  const orderTemplateName = useMemo(() => {
    if (orderTemplateId && orderTemplates.length > 0) {
      const selectedOrderTemplate = orderTemplates.find(
        (ot) => ot.id === orderTemplateId
      );
      return selectedOrderTemplate?.name ?? "";
    }
    return "";
  }, [orderTemplateId, orderTemplates]);

  const workOrderTemplateActions = {
    cancel: {
      label: BUTTON_LABELS.CANCEL,
      onClick: () => {
        reset();
        goBack();
      },
    },
    submit: {
      label: `Save ${ENTITY_NAMES.WORK_ORDER_TEMPLATE}`,
      onClick: () => {
        setOpenConfirmModal(true);
      },
      disabled: !isValid || !isDirty || isLoading,
    },
  };

  const tabs = useMemo<IVerticalTabProps<IWorkOrderTemplateData>[]>(
    () => [
      {
        label: "Work Order Details",
        value: "work order details",
        controlledFormViewComponent: WorkOrderDetailsTabView,
        isComplete: isValid,
        hasErrors: !isValid,
        extraTabProps: {
          orderTemplates,
          calendarSets: selectedCalendarSets,
          isReadOnly,
        },
      },
      {
        label: "Work Order Step(s)",
        value: "work order steps",
        controlledFormViewComponent: WorkOrderStepsTabView,
        extraTabProps: {
          isReadOnly,
        },
        disabled: !id,
      },
      {
        label: "Production Capacity",
        value: "work order customer exclusion",
        controlledFormViewComponent: WorkOrderCustomerExclusionTabView,
        extraTabProps: {
          isReadOnly,
          accounts,
          excludedAccounts,
          excludeWorkOrderTemplate,
          reenableWorkOrderTemplate,
        },
        disabled: !id,
      },
      {
        label: "Assignment Rules",
        value: "work order assignment rules",
        controlledFormViewComponent: WorkOrderAssignmentRulesTabView,
        extraTabProps: {
          isReadOnly,
          calendarSets: selectedCalendarSets,
        },
        disabled: !id,
      },
      {
        label: "Scheduling Rules",
        value: "work order scheduling rules",
        controlledFormViewComponent: WorkOrderSchedulingRulesTabView,
        extraTabProps: {
          isReadOnly,
          calendarSets: selectedCalendarSets,
        },
        disabled: !id,
      },
    ],
    [
      isValid,
      orderTemplates,
      selectedCalendarSets,
      isReadOnly,
      accounts,
      excludedAccounts,
      id,
    ]
  );

  const handleSubmitWorkOrderTemplate = async (isActive: boolean) => {
    const data = { ...getValues(), isActive };

    if (workOrderTemplateId) {
      const updatedData = getUpdatedFields(
        dirtyFields,
        getValues()
      ) as Partial<IWorkOrderTemplateData>;

      const autoAssignRelatedFields =
        dirtyFields.autoAssign && autoAssign
          ? {
              resourceAssignments: data.resourceAssignments,
            }
          : {};

      updateWorkOrderTemplate(
        {
          ...updatedData,
          ...autoAssignRelatedFields,
          id: data.id,
          modifiedOn: data.modifiedOn,
          isActive,
        },
        data.name,
        reset
      );
      if (updatedData.steps) {
        await reorderStepTemplates(
          updatedData.steps,
          assignmentTemplateModifiedOn
        );
        await getStepTemplates();
      }
    } else {
      handleCreateWorkOrderTemplate(data);
    }
    setOpenConfirmModal(false);
  };

  const watchName = useWatch({
    control: form.control,
    name: "name",
  });

  const renderPageTitle = () => {
    const action = workOrderTemplateId ? "Editing" : "Creating";
    const workOrderTemplateLabel = watchName
      ? `${watchName}`
      : `New ${ENTITY_NAMES.WORK_ORDER_TEMPLATE}`;
    return (
      <>
        <Typography variant="HD4">
          {action} {ENTITY_NAMES.WORK_ORDER_TEMPLATE}:{" "}
          <Typography variant="HD4" color="primary" component="span">
            {workOrderTemplateLabel}
          </Typography>
        </Typography>
        {orderTemplateName && (
          <Chip
            label={`Attached To ${orderTemplateName}`}
            size="small"
            variant="outlined"
            sx={{
              ml: 2,
              borderRadius: 1,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              fontWeight: theme.fontWeight.semiBold,
              backgroundColor: theme.palette.primary.light,
            }}
          />
        )}
      </>
    );
  };

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={workOrderTemplateActions}
      loading={isLoading}
      titleActionComponent={
        id ? (
          <ConfigMenu
            data={[
              {
                label: isActive ? "Draft" : "Activate",
                action: () => {
                  const data = getValues();
                  updateWorkOrderTemplate(
                    {
                      isActive: !isActive,
                      id,
                      modifiedOn: data.modifiedOn,
                    },
                    data.name,
                    reset
                  );
                },
              },
            ]}
            optionalElement={{
              size: "medium",
              title: isActive ? "Status: Active" : "Status: Draft",
              color: isActive ? "success" : "orange",
              endIcon: { path: mdiChevronDown },
              sx: { width: 85 },
            }}
          />
        ) : undefined
      }
    >
      <VerticalTabsLayout tabs={tabs} form={form} transparentBackground />
      <MiModal
        title={`You Are Saving Your ${ENTITY_NAMES.WORK_ORDER_TEMPLATE}`}
        description={
          <>
            You are saving{" "}
            <span
              style={{
                color: theme.palette.primary.main,
                fontWeight: theme.fontWeight.semiBold,
              }}
            >
              {name}
            </span>
            . Would you like to activate this{" "}
            {ENTITY_NAMES.WORK_ORDER_TEMPLATE.toLowerCase()} to be used in your
            org?
          </>
        }
        titleIcon={mdiAlertCircleOutline}
        leftIconColor={theme.palette.primary.main}
        open={openConfirmModal}
        closeModal={() => setOpenConfirmModal(false)}
        actions={[
          {
            name: "Save As Draft",
            color: BUTTON_COLORS.ORANGE,
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => handleSubmitWorkOrderTemplate(false),
          },
          {
            name: isActive ? "Save Type" : "Activate Type",
            color: BUTTON_COLORS.SUCCESS,
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => handleSubmitWorkOrderTemplate(true),
          },
        ]}
      />
    </ConfigPageLayout>
  );
}

const defaultWorkOrderTemplateData: IWorkOrderTemplateData = {
  name: "",
  shortName: "",
  abbreviation: "",
  description: "",
  color: "",
  noStepsRequired: false,
  orderTemplateId: "",
  tradeId: "",
  module: null,
  isActive: false,
  workType: "Office",
  steps: [],
  calendarSetId: null,
  calendarCuttoffTime: null,
  autoAssign: false,
  resourceAssignments: [],
  allDayAssignment: true,
  autoAssignmentWindows: [],
};
